import type { StorageApi } from '@backstage/core-plugin-api';
import { APPEARANCE_BUCKET } from '../theme/palette';

/**
 * Fun Mode — a per-user boolean that swaps every keyed UI string to its playful
 * `fun` variant (see ./catalog + ./useT). Persisted like the palette: a
 * synchronous localStorage mirror (read pre-paint) plus the backend-backed
 * `appearance` StorageApi bucket, reconciled on load / cross-device.
 */
const FUN_MODE_STORAGE = 'regask-fun-mode';
export const FUN_MODE_KEY = 'funMode';
export const FUN_MODE_CHANGE_EVENT = 'regask-fun-mode-change';

/** Synchronous fun-mode flag from the localStorage mirror (default false). */
export function getStoredFunMode(): boolean {
  try {
    return localStorage.getItem(FUN_MODE_STORAGE) === 'true';
  } catch {
    return false;
  }
}

/** Persist to the mirror + notify subscribers. */
export function setStoredFunMode(on: boolean): void {
  try {
    if (on) localStorage.setItem(FUN_MODE_STORAGE, 'true');
    else localStorage.removeItem(FUN_MODE_STORAGE);
  } catch {
    // Private mode / storage disabled — fall through to session-only.
  }
  window.dispatchEvent(new CustomEvent(FUN_MODE_CHANGE_EVENT, { detail: on }));
}

/** Subscribe to fun-mode changes; returns an unsubscribe fn. */
export function subscribeFunMode(cb: () => void): () => void {
  window.addEventListener(FUN_MODE_CHANGE_EVENT, cb);
  return () => window.removeEventListener(FUN_MODE_CHANGE_EVENT, cb);
}

function appearanceBucket(storage: StorageApi): StorageApi {
  return storage.forBucket(APPEARANCE_BUCKET);
}

/** Persist the choice server-side (per user). */
export function persistFunModeToBackend(
  storage: StorageApi,
  on: boolean,
): void {
  void appearanceBucket(storage).set<boolean>(FUN_MODE_KEY, on);
}

/**
 * Apply the server-stored flag on load + cross-device/tab changes, keeping the
 * mirror + subscribers in sync. Only acts on a *present* value (mirrors
 * subscribePaletteBackend), so an absent value never resets a local choice.
 */
export function subscribeFunModeBackend(storage: StorageApi): () => void {
  const sub = appearanceBucket(storage)
    .observe$<boolean>(FUN_MODE_KEY)
    .subscribe(snapshot => {
      if (snapshot.presence !== 'present') return;
      if (snapshot.value !== getStoredFunMode())
        setStoredFunMode(snapshot.value);
    });
  return () => sub.unsubscribe();
}
